import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "../lib/supabase";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import {
  isSessionMissingAuthError,
  isTimeoutLikeAuthError,
} from "@/lib/auth-errors";
import { takeReturnPath } from "@/lib/auth-return-path";

const passwordSchema = z
  .object({
    password: z
      .string()
      .min(6, { message: "Password must be at least 6 characters" }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  /** Why the link failed, so we can say "expired" instead of a generic error. */
  const [linkFailure, setLinkFailure] = useState<"expired" | "invalid">(
    "invalid"
  );
  /**
   * Shown once the update has been in flight long enough to look stuck. The
   * request itself is bounded by the Supabase client (auth calls time out), so
   * this only tells the user what is happening instead of guessing the outcome.
   */
  const [slowRequest, setSlowRequest] = useState(false);

  const timeoutMessage = () =>
    t(
      "auth.resetPasswordTimeout",
      language === "de"
        ? "Die Verbindung zum Server hat zu lange gedauert. Prüfe deine Internetverbindung und versuche es erneut. Falls du coliviq in einem anderen Tab offen hast, schließe diesen zuerst."
        : "The server took too long to respond. Check your connection and try again — if you have coliviq open in another tab, close it first."
    );

  useEffect(() => {
    const handleRecovery = async () => {
      // Supabase delivers recovery tokens in the URL hash, e.g.
      // /reset-password#access_token=xxx&refresh_token=xxx&type=recovery
      const hash = window.location.hash.slice(1);
      const hashParams = new URLSearchParams(hash);
      const queryParams = new URLSearchParams(window.location.search);

      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      // token_hash / type arrive as query params from send-password-reset;
      // the legacy /auth/v1/verify bounce puts everything in the hash.
      const type = queryParams.get("type") ?? hashParams.get("type");
      const tokenHash =
        queryParams.get("token_hash") ?? hashParams.get("token_hash");
      const errorCode =
        queryParams.get("error_code") ?? hashParams.get("error_code");

      if (errorCode) {
        console.error(
          "Recovery error from Supabase:",
          queryParams.get("error_description") ??
            hashParams.get("error_description")
        );
        setLinkFailure(errorCode === "otp_expired" ? "expired" : "invalid");
        setIsValidToken(false);
        return;
      }

      // Current flow: the emailed link points here with a one-time token hash
      // that is only spent by this call — nothing is consumed by a mail
      // client pre-fetching the URL.
      if (tokenHash && (type ?? "recovery") === "recovery") {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type: "recovery",
          token_hash: tokenHash,
        });
        if (verifyError) {
          console.error("Error verifying recovery token:", verifyError);
          setLinkFailure(
            /expired|invalid/i.test(verifyError.message) ? "expired" : "invalid"
          );
          setIsValidToken(false);
          return;
        }
        setIsValidToken(true);
        // Drop the token from the address bar so it can't be re-shared or
        // replayed from history.
        window.history.replaceState({}, "", "/reset-password");
        return;
      }

      if (type === "recovery" && accessToken && refreshToken) {
        try {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            console.error("Error setting recovery session:", sessionError);
            setIsValidToken(false);
          } else {
            setIsValidToken(true);
            window.history.replaceState({}, "", "/reset-password");
          }
        } catch (error) {
          console.error("Error handling recovery:", error);
          setIsValidToken(false);
        }
      } else {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user) {
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
        }
      }
    };

    handleRecovery();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError({});
    let slowTimer: ReturnType<typeof setTimeout> | undefined;

    try {
      passwordSchema.parse({ password, confirmPassword });
      setLoading(true);
      slowTimer = setTimeout(() => setSlowRequest(true), 8000);

      const { error: resetError } = await resetPassword(password);

      if (resetError) {
        // A recovery session that is gone (expired link, or spent in another
        // tab) comes back as "Auth session missing" / an expired JWT. Sending
        // the user to "request a new link" is actionable; the raw message isn't.
        if (isSessionMissingAuthError(resetError)) {
          setLinkFailure("expired");
          setIsValidToken(false);
          return;
        }
        setError({
          password: isTimeoutLikeAuthError(resetError)
            ? timeoutMessage()
            : resetError.message || "Failed to reset password",
        });
      } else {
        toast({
          title: "Success",
          description:
            "Your password has been reset successfully. You can now sign in with your new password.",
        });
        // This recovery session skips signIn() entirely, so it's the other
        // place (besides signIn()) that needs to finish a bounced deep link —
        // e.g. /dashboard?tab=checkin from a move-out email. Conservative
        // canAccessAdmin: false, same as signIn()'s own no-uid fallback: we
        // don't have a fresh role read here, and this reset flow is a tenant
        // path, not an admin one.
        const returnPath = takeReturnPath({ canAccessAdmin: false });
        setTimeout(() => {
          navigate(returnPath ?? "/dashboard");
        }, 2000);
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setError(newErrors);
      } else {
        setError({
          password: isTimeoutLikeAuthError(err)
            ? timeoutMessage()
            : "An unexpected error occurred",
        });
      }
    } finally {
      if (slowTimer) clearTimeout(slowTimer);
      setSlowRequest(false);
      setLoading(false);
    }
  };

  if (isValidToken === null) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">
              {linkFailure === "expired"
                ? t(
                    "auth.expiredResetLink",
                    language === "de"
                      ? "Dieser Link ist abgelaufen"
                      : "This reset link has expired"
                  )
                : t("auth.invalidResetLink")}
            </CardTitle>
            <CardDescription>
              {linkFailure === "expired"
                ? t(
                    "auth.expiredResetLinkDesc",
                    language === "de"
                      ? "Links zum Zurücksetzen sind 1 Stunde gültig und können nur einmal verwendet werden. Fordere einen neuen an – er ist innerhalb einer Minute in deinem Postfach."
                      : "Reset links are valid for 1 hour and can only be used once. Request a new one and it will arrive in your inbox within a minute."
                  )
                : t("auth.invalidResetLinkDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Link to="/forgot-password">
                <Button className="w-full">{t("auth.requestNewLink")}</Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  {t("auth.backToHome")}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex-1">
              <CardTitle className="text-2xl font-bold">
                {t("auth.resetPasswordTitle")}
              </CardTitle>
              <CardDescription>
                {t("auth.resetPasswordSubtitle")}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">{t("auth.newPassword")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t(
                    "public.resetPassword.enterNewPassword",
                    "Enter new password"
                  )}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {error.password && (
                <p className="text-sm text-destructive">{error.password}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {t("auth.confirmNewPassword")}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t(
                    "public.resetPassword.confirmNewPassword",
                    "Confirm new password"
                  )}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {error.confirmPassword && (
                <p className="text-sm text-destructive">
                  {error.confirmPassword}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("auth.resettingPassword") : t("auth.resetPassword")}
            </Button>

            {slowRequest && (
              <p className="text-sm text-muted-foreground text-center">
                {t(
                  "auth.resetPasswordSlow",
                  language === "de"
                    ? "Das dauert länger als gewöhnlich – wir warten noch auf den Server."
                    : "This is taking longer than usual — still waiting for the server."
                )}
              </p>
            )}

            <div className="text-center">
              <Link to="/" className="text-sm text-primary hover:underline">
                {t("auth.backToLogin")}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
